import {
  createStyles,
  Typography,
  type WithStyles,
  withStyles,
} from "@material-ui/core";
import { useTranslations } from "adapters/i18n";
import { DATE_PICKER_MASK } from "constants/global";
import { DatePicker, type MaterialUiPickersDate } from "material-ui-pickers";
import React, { type ReactNode } from "react";
import { DATE_FORMATS, DateUtils } from "shared/dateUtils";
import type { LivestockInfoFromFarm } from "shared/types";

type WeighingDateColumnCellProps = {
  error: string | null | undefined;
  onChange: (date: string | null, row: LivestockInfoFromFarm) => void;
  onError: (error: string | null, row: LivestockInfoFromFarm) => void;
  row: LivestockInfoFromFarm;
  value: string | null;
} & WithStyles<typeof styles>;

function WeighingDateColumnCell({
  classes,
  error,
  onChange,
  onError,
  row,
  value,
}: WeighingDateColumnCellProps) {
  const { t } = useTranslations();

  const handleChange = (date: MaterialUiPickersDate) => {
    if (date == null || !date.isValid()) {
      onChange(null, row);
      return;
    }

    onChange(date.toISOString(), row);
  };

  const handleError = (_: unknown, error: ReactNode) => {
    // Safely cast this type as maxDateMessage and invalidDateMessage
    // are the only possible errors and they are strings
    onError(error as string | null, row);
  };

  const handleCleanInput = () => {
    onChange(null, row);
  };

  return (
    <div className={classes.root}>
      <DatePicker
        aria-invalid={!!error}
        className={classes.picker}
        clearable={true}
        data-testid="lot-weighing-date-picker"
        disableFuture={true}
        disableOpenOnEnter={true}
        error={!!error}
        format={DATE_FORMATS["DD/MM/YYYY"]}
        helperText={error}
        invalidDateMessage={t("common:validation.invalid-date-format")}
        keyboard={true}
        mask={(value) => (value ? DATE_PICKER_MASK : [])}
        maxDateMessage={t("common:validation.max-date-final")}
        onChange={handleChange}
        onClear={handleCleanInput}
        onEmptied={handleCleanInput}
        onError={handleError}
        placeholder="dd/mm/aaaa"
        value={value}
      />
      <Typography
        className={classes.text}
        component="span"
        data-testid="lot-weighing-date-text"
      >
        {value != null
          ? DateUtils.parse(value).format(DATE_FORMATS["DD/MM/YYYY"])
          : "-"}
      </Typography>
    </div>
  );
}

const styles = () =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      width: "240px",
      "@media print": {
        width: "min-content",
      },
    },
    picker: {
      fontSize: "8px",
      maxWidth: 150,
      minWidth: 140,
      opacity: "0.9",
      paddingTop: "15px",
      width: "100%",
      "@media print": {
        display: "none",
      },
    },
    text: {
      display: "none",
      "@media print": {
        display: "block",
        fontSize: "inherit",
      },
    },
  });

export default withStyles(styles)(WeighingDateColumnCell);
